import type { TFunction } from "i18next";
import type { TourStep } from "../TourProvider";
import { ROUTES } from "~/utils/routing/routeUtils";
import { queryTourTarget } from "../utils/queryTourTarget";

/**
 * Get the current active tab from the URL or DOM
 */
const getCurrentTab = (): string => {
  const urlParams = new URLSearchParams(window.location.search);
  const tabFromUrl = urlParams.get("tab");
  if (tabFromUrl) {
    return tabFromUrl;
  }

  const activeTab = document.querySelector(
    ".ant-tabs-tab-active"
  ) as HTMLElement | null;
  if (activeTab) {
    return activeTab.getAttribute("data-node-key") || "details";
  }

  return "details";
};

/**
 * Tab order for navigation — includes Unit Plans
 */
const TAB_ORDER = [
  "details",
  "buildingList",
  "amenities",
  "unitPlans",
  "serviceRequests",
];

/**
 * Details Tab Tour Steps (curated)
 */
const getDetailsTabSteps = (t: TFunction): TourStep[] => [
  {
    title: t("Tour_PropertyDetails_Welcome_Title"),
    description: t("Tour_PropertyDetails_Welcome_Description"),
    target: () => queryTourTarget("property-details-header"),
    placement: "bottom",
  },
  {
    title: t("Tour_PropertyDetails_Invite_Title"),
    description: t("Tour_PropertyDetails_Invite_Description"),
    target: () => queryTourTarget("property-invite-tenant"),
    placement: "left",
  },
  {
    title: t("Tour_PropertyDetails_Tabs_Title"),
    description: t("Tour_PropertyDetails_Tabs_Description"),
    target: () => queryTourTarget("property-details-tabs"),
    placement: "bottom",
  },
  {
    title: t("Tour_PropertyDetails_Overview_Title"),
    description: t("Tour_PropertyDetails_Overview_Description"),
    target: () => queryTourTarget("property-details-tab-content"),
    placement: "top",
  },
  {
    title: t("Tour_PropertyDetails_Actions_Title"),
    description: t("Tour_PropertyDetails_Actions_Description"),
    target: () => queryTourTarget("property-action-buttons"),
    placement: "bottom",
    tabNavigation: {
      currentTab: "details",
      tabOrder: TAB_ORDER,
    },
  },
];

/**
 * BuildingList Tab Tour Steps
 */
const getBuildingListTabSteps = (t: TFunction): TourStep[] => [
  {
    title: t("Tour_PropertyDetails_Buildings_Title"),
    description: t("Tour_PropertyDetails_Buildings_Description"),
    target: () => queryTourTarget("property-buildings-tab-content"),
    placement: "top",
  },
  {
    title: t("Tour_PropertyDetails_AddBuilding_Title"),
    description: t("Tour_PropertyDetails_AddBuilding_Description"),
    target: () => queryTourTarget("property-add-building-button"),
    placement: "left",
    tabNavigation: {
      currentTab: "buildingList",
      tabOrder: TAB_ORDER,
    },
  },
];

/**
 * Amenities Tab Tour Steps
 */
const getAmenitiesTabSteps = (t: TFunction): TourStep[] => [
  {
    title: t("Tour_PropertyDetails_Amenities_Title"),
    description: t("Tour_PropertyDetails_Amenities_Description"),
    target: () => queryTourTarget("property-amenities-tab-content"),
    placement: "top",
  },
  {
    title: t("Tour_PropertyDetails_AddAmenity_Title"),
    description: t("Tour_PropertyDetails_AddAmenity_Description"),
    target: () => queryTourTarget("property-add-amenity-button"),
    placement: "left",
    tabNavigation: {
      currentTab: "amenities",
      tabOrder: TAB_ORDER,
    },
  },
];

/**
 * Unit Plans Tab Tour Steps
 */
const getUnitPlansTabSteps = (t: TFunction): TourStep[] => [
  {
    title: t("Tour_PropertyDetails_UnitPlans_Title"),
    description: t("Tour_PropertyDetails_UnitPlans_Description"),
    target: () => queryTourTarget("property-unit-plans-tab-content"),
    placement: "top",
  },
  {
    title: t("Tour_PropertyDetails_AddUnitPlan_Title"),
    description: t("Tour_PropertyDetails_AddUnitPlan_Description"),
    target: () => queryTourTarget("property-add-unit-plan-button"),
    placement: "left",
    tabNavigation: {
      currentTab: "unitPlans",
      tabOrder: TAB_ORDER,
    },
  },
];

/**
 * Service Requests Tab Tour Steps
 */
const getServiceRequestsTabSteps = (t: TFunction): TourStep[] => [
  {
    title: t("Tour_PropertyDetails_ServiceRequests_Title"),
    description: t("Tour_PropertyDetails_ServiceRequests_Description"),
    target: () => queryTourTarget("property-service-requests-tab-content"),
    placement: "top",
    tabNavigation: {
      currentTab: "serviceRequests",
      tabOrder: TAB_ORDER,
    },
    recommendedTours: [
      {
        label: t("Tour_Recommended_PropertyTable"),
        path: ROUTES.propertyTable,
      },
    ],
  },
];

/**
 * Tour configuration for the Property Details Page (/property/:propertyID)
 *
 * Tab-aware refresher covering Details, Buildings, Amenities, Unit Plans,
 * and Service Requests.
 */
export const propertyDetailsTour = (t: TFunction): TourStep[] => {
  const currentTab = getCurrentTab();

  switch (currentTab) {
    case "details":
      return getDetailsTabSteps(t);
    case "buildingList":
      return getBuildingListTabSteps(t);
    case "amenities":
      return getAmenitiesTabSteps(t);
    case "unitPlans":
      return getUnitPlansTabSteps(t);
    case "serviceRequests":
      return getServiceRequestsTabSteps(t);
    default:
      return getDetailsTabSteps(t);
  }
};
